<template>
  <div style="margin: 0 20px 20px;">
    <div class="ad-header-row d-flex align-items-center justify-content-center flex-wrap">
      <div class="ad-header-left">
        <span style="font-size: 24px;">Alerts Dashboard</span>
      </div>
      <a href="#" class="white-link" v-on:click.prevent="shift_date(-1)">&lt;</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_date(1)">&gt;</a>
      &nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
      <div class="ad-header-right">
        <b-button :to="{ name: 'alert_list' }" size="sm" variant="primary">
          <i class="fa fa-history" style="margin-right: 6px;"></i>Alert History
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center" style="margin-top: 40px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <template v-if="!loading">
      <!-- KPI row -->
      <div class="row" style="margin: 25px 10px 25px 10px; font-weight: bold; font-size: 18px; align-items: center;">
        <div v-if="summary.total_cnt > 0" class="col" style="border: 1px solid grey;">Total Alerts: {{summary.total_cnt}}</div>
        <div v-if="summary.email_cnt > 0" class="col" style="border: 1px solid grey;">Emails Sent: {{summary.email_cnt}}</div>
        <div v-if="summary.sms_cnt > 0" class="col" style="border: 1px solid grey;">SMS Sent: {{summary.sms_cnt}}</div>
        <div v-if="summary.voice_cnt > 0" class="col" style="border: 1px solid grey;">Voice Sent: {{summary.voice_cnt}}</div>
        <div v-if="summary.sms_cnt > 0" class="col" style="border: 1px solid grey;">SMS Cost: ${{summary.sms_cost}}</div>
        <div class="col" :class="successRateClass" style="border: 1px solid grey;">Success Rate: {{successRatePct != null ? successRatePct + '%' : 'n/a'}}</div>
        <div v-if="items_full.length" class="ad-chart-type-toggle ml-auto">
          <b-button
            size="sm"
            :variant="timeSeriesChartType === 'ColumnChart' ? 'primary' : 'outline-secondary'"
            @click="timeSeriesChartType = 'ColumnChart'"
          >
            <i class="fa fa-bar-chart" style="margin-right: 4px;"></i>Bar
          </b-button>
          <b-button
            size="sm"
            :variant="timeSeriesChartType === 'LineChart' ? 'primary' : 'outline-secondary'"
            @click="timeSeriesChartType = 'LineChart'"
          >
            <i class="fa fa-line-chart" style="margin-right: 4px;"></i>Line
          </b-button>
        </div>
      </div>

      <div v-if="!items_full.length" class="text-center" style="margin-top: 60px; color: #888;">
        <h4>No alerts found for this date range.</h4>
      </div>

      <template v-else>
        <div class="ad-top-charts-row ad-top-charts-row--fill">
          <!-- Alerts over time, by type -->
          <dashboard-chart
            :loading="loading"
            :chart_data="alertsOverTimeChart"
            :chart_type="timeSeriesChartType"
            graph_title="Alerts By Type"
            date_format=""
            graph_height="340"
            :chart_colors="overTimeColors"
            :stacked="timeSeriesChartType === 'ColumnChart'"
            :curved="timeSeriesChartType === 'LineChart'"
            :fill_height="true"
          />

          <div class="ad-chart-card ad-chart-card--fill">
            <div class="ad-chart-title" style="margin-bottom: 0;">Alert Counts by Type</div>
            <div class="ad-chart-fill-area">
              <GChart
                v-if="alertsByTypeChart"
                class="ad-fill-gchart"
                type="BarChart"
                :data="alertsByTypeChart"
                :options="barOptionsFill(alertsByTypeChart)"
                :settings="{ packages: ['corechart'] }"
              />
              <div v-else class="ad-chart-empty">-no data available-</div>
            </div>
          </div>
        </div>

        <div class="ad-top-charts-row">
          <!-- Success/fail delivery outcome, by channel -->
          <dashboard-chart
            :loading="loading"
            :chart_data="deliveryOutcomeOverTimeChart"
            :chart_type="timeSeriesChartType"
            graph_title="Delivery Status"
            date_format=""
            graph_height="340"
            :chart_colors="deliveryOutcomeColors"
            :stacked="timeSeriesChartType === 'ColumnChart'"
            :curved="timeSeriesChartType === 'LineChart'"
            :fill_height="true"
          />

          <!-- Failures only, by distinct reason -->
          <dashboard-chart
            :loading="loading"
            :chart_data="deliveryFailureReasonsOverTimeChart"
            :chart_type="timeSeriesChartType"
            graph_title="Delivery Failure Reasons"
            date_format=""
            graph_height="340"
            :chart_colors="overTimeColors"
            :stacked="timeSeriesChartType === 'ColumnChart'"
            :curved="timeSeriesChartType === 'LineChart'"
            :fill_height="true"
          />
        </div>

        <!-- Breakdown grid: top vehicles/drivers -->
        <div class="ad-chart-grid">

          <div v-if="lateAlertsByDriverChart" class="ad-chart-card ad-chart-card--wide">
            <div class="ad-chart-title">Most Late Alerts by Driver</div>
            <GChart
              type="BarChart"
              :data="lateAlertsByDriverChart"
              :options="barOptions(lateAlertsByDriverChart)"
              :settings="{ packages: ['corechart'] }"
            />
          </div>

          <div v-if="dvirAlertsByDriverChart" class="ad-chart-card ad-chart-card--wide">
            <div class="ad-chart-title">Most DVIR Alerts by Driver</div>
            <GChart
              type="BarChart"
              :data="dvirAlertsByDriverChart"
              :options="barOptions(dvirAlertsByDriverChart)"
              :settings="{ packages: ['corechart'] }"
            />
          </div>

        </div>

        <div class="ad-chart-card" style="margin-top: 25px;">
          <div class="ad-chart-title">Recent Delivery Errors</div>
          <table v-if="recentFailures.length" class="ad-mini-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Alert Type</th>
                <th>Booking ID</th>
                <th>Vehicle ID</th>
                <th>Recipient</th>
                <th>Phone / Email</th>
                <th>Reason</th>
                <th>Message Preview</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in recentFailures" :key="f.key">
                <td class="ad-failure-time">{{f.timeLabel}}</td>
                <td>{{f.alert_type}}</td>
                <td>{{f.booking_id}}</td>
                <td>{{f.vehicle_id}}</td>
                <td>{{f.recipient}}</td>
                <td>{{f.contact}}</td>
                <td class="ad-failure-reason">{{f.reason}}</td>
                <td class="ad-failure-preview">{{f.preview}}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="ad-chart-empty">No delivery failures in this range</div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { GChart } from 'vue-google-charts'
import DashboardChart from './DashboardChart'
import { get_alerts } from '../networking/apis'
import { mapState } from 'vuex'
import moment from 'moment'
import { justDate } from '@/helpers/dates'

// wide enough to give every alert type its own color on the over-time chart
const TYPE_COLOR_PALETTE = [
  'steelblue', 'orange', 'green', 'purple', 'darkred', 'goldenrod', 'teal',
  'crimson', 'navy', 'olive', 'brown', 'magenta', 'darkcyan', 'coral',
  'slateblue', 'darkgreen', 'firebrick', 'indigo', 'chocolate', 'dimgray',
]

export default {
  name: 'alerts_dashboard',
  components: {
    GChart,
    DashboardChart,
  },
  data() {
    return {
      loading: true,
      items_full: [],
      summary: {},
      timeSeriesChartType: 'ColumnChart',
      details_fromDate: justDate(moment().subtract(29, 'days')),
      details_toDate: justDate(moment()),
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(360, 'days'),
        maxDate: moment(),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },
    }
  },
  async created() {
    this.load_data()
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        const { value: { data = [] } } = await get_alerts(
          this.user.tenant,
          '',
          moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        )
        this.summary = data.summary || {}
        if (this.summary.timezone) moment.tz.setDefault(this.summary.timezone)
        this.items_full = data.alerts || []
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data', 'danger', 5000)
      }
      this.loading = false
    },
    shift_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.load_data()
    },
    // mirrors the "Problems Only" definition on the Alert History page, so a
    // delivery counts as failed the same way in both places.
    getLatestEmailStatus(item) {
      if (!item.email_msg_status || !item.email_msg_status.length) return null
      return item.email_msg_status.reduce((a, b) => (new Date(a.datetime) > new Date(b.datetime) ? a : b))
    },
    isAlertFailed(item) {
      const emailStatus = this.getLatestEmailStatus(item)
      const hasEmailIssue = emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())
      const hasSmsIssue = item.provider_response && !item.provider_response.includes('Message has been accepted')
      return !!(hasEmailIssue || hasSmsIssue)
    },
    // null when the item never attempted that channel, so it's excluded from that channel's counts
    emailOutcome(item) {
      const emailStatus = this.getLatestEmailStatus(item)
      if (!emailStatus) return null
      return ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase()) ? 'Email Fail' : 'Email Success'
    },
    smsOutcome(item) {
      if (!item.provider_response) return null
      return item.provider_response.includes('Message has been accepted') ? 'SMS Success' : 'SMS Fail'
    },
    // distinct reason strings an item can match - raw sms provider response text
    // (varies by carrier) plus the decoded email status; an item can match more
    // than one (e.g. both its sms and email attempts failed)
    failureReasonsFor(item) {
      const reasons = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        reasons.push(this.normalizeSmsReason(item.provider_response))
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        reasons.push(this.decodeEmailStatus(emailStatus.status))
      }
      return reasons
    },
    // "+12125864095 is opted out" varies per phone number, which would otherwise
    // fragment the opt-out cases into one series per number - strip the number so
    // they all group under a single "opted out" series
    normalizeSmsReason(raw) {
      const text = raw.trim()
      if (/opted out/i.test(text)) return 'opted out'
      return text
    },
    decodeEmailStatus(rawStatus) {
      const map = {
        send: 'sent',
        delivery: 'delivered',
        bounce: 'bounced',
        complaint: 'filed complaint',
        open: 'opened',
        click: 'clicked link',
      }
      return map[(rawStatus || '').toLowerCase()] || rawStatus
    },
    failureReason(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        parts.push(`sms: ${item.provider_response}`)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        parts.push(`email: ${this.decodeEmailStatus(emailStatus.status)}`)
      }
      return parts.join(' | ') || 'unknown'
    },
    // the phone number and/or email address that actually bounced/failed, as opposed
    // to failureReason() which describes why
    failureContact(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        // driver/leader recipients live in sms_drivers/sms_leaders rather than
        // the plain sms field, so sms_all_recipients is the one that's actually
        // populated for the driver-facing alert types (LateAlert, MissingDVIR, ...)
        const phone = item.sms_all_recipients || item.sms || item.sms_drivers || item.sms_leaders
        if (phone) parts.push(phone)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase()) && item.email) {
        parts.push(item.email)
      }
      return parts.join(' | ')
    },
    firstLine(item) {
      const text = item.message || item.email_message || item.email_subject || ''
      const line = String(text).split('\n')[0].trim()
      return line.length > 80 ? line.slice(0, 80) + '…' : line
    },
    barOptions(chart_data, height = 320) {
      const maxLen = chart_data.slice(1).reduce((m, r) => Math.max(m, String(r[0]).length), 0)
      const pct = Math.min(45, Math.max(18, Math.ceil(maxLen * 0.85)))
      return {
        height,
        legend: { position: 'none' },
        backgroundColor: { fill: 'transparent' },
        colors: ['steelblue'],
        chartArea: { left: `${pct}%`, right: 40, top: 20, bottom: 10 },
        // value axis is redundant now that counts are shown as bar annotations
        hAxis: { textPosition: 'none', gridlines: { color: 'transparent' }, baselineColor: 'transparent' },
        vAxis: { textStyle: { color: 'black' } },
        annotations: { alwaysOutside: true, textStyle: { fontSize: 12, color: '#333' } },
      }
    },
    // same as barOptions(), but omits the fixed height so the chart fills
    // its flex:1 container (see .ad-fill-gchart) instead of a guessed pixel value
    barOptionsFill(chart_data) {
      const opts = this.barOptions(chart_data)
      delete opts.height
      return opts
    },
    // builds a [label, value] bar chart data table with a count annotation on each bar
    barChartData(labelHeader, valueHeader, rows) {
      return [
        [labelHeader, valueHeader, { role: 'annotation' }],
        ...rows.map(([label, count]) => [label, count, String(count)]),
      ]
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    failedCount() {
      return this.items_full.filter(item => this.isAlertFailed(item)).length
    },
    successRatePct() {
      const total = this.items_full.length
      if (!total) return null
      return Math.round(((total - this.failedCount) / total) * 1000) / 10
    },
    successRateClass() {
      if (this.successRatePct == null) return ''
      if (this.successRatePct >= 95) return 'success'
      if (this.successRatePct >= 80) return ''
      return 'error'
    },
    alertsOverTimeChart() {
      if (!this.items_full.length) return null
      const byDay = {}
      const totalsByType = {}
      this.items_full.forEach(item => {
        if (!item.processed_time) return
        const day = moment(item.processed_time).format('YYYY-MM-DD')
        const type = item.alert_type || 'Unknown'
        totalsByType[type] = (totalsByType[type] || 0) + 1
        if (!byDay[day]) byDay[day] = {}
        byDay[day][type] = (byDay[day][type] || 0) + 1
      })
      const days = Object.keys(byDay).sort()
      const typeList = Object.entries(totalsByType)
        .sort((a, b) => b[1] - a[1])
        .map(([type]) => type)
      return [
        ['Date', ...typeList],
        ...days.map(day => [moment(day).format('MM/DD'), ...typeList.map(t => byDay[day][t] || 0)]),
      ]
    },
    overTimeColors() {
      return TYPE_COLOR_PALETTE
    },
    deliveryOutcomeOverTimeChart() {
      if (!this.items_full.length) return null
      const cols = ['Email Success', 'Email Fail', 'SMS Success', 'SMS Fail']
      const byDay = {}
      this.items_full.forEach(item => {
        if (!item.processed_time) return
        const day = moment(item.processed_time).format('YYYY-MM-DD')
        if (!byDay[day]) byDay[day] = { 'Email Success': 0, 'Email Fail': 0, 'SMS Success': 0, 'SMS Fail': 0 }
        const emailOutcome = this.emailOutcome(item)
        if (emailOutcome) byDay[day][emailOutcome]++
        const smsOutcome = this.smsOutcome(item)
        if (smsOutcome) byDay[day][smsOutcome]++
      })
      const days = Object.keys(byDay).sort()
      if (!days.length) return null
      return [
        ['Date', ...cols],
        ...days.map(day => [moment(day).format('MM/DD'), ...cols.map(c => byDay[day][c])]),
      ]
    },
    deliveryOutcomeColors() {
      return ['#4a90d9', '#c0392b', '#7ed321', '#e67e22']
    },
    deliveryFailureReasonsOverTimeChart() {
      if (!this.items_full.length) return null
      const byDay = {}
      const totalsByReason = {}
      this.items_full.forEach(item => {
        if (!item.processed_time) return
        const day = moment(item.processed_time).format('YYYY-MM-DD')
        this.failureReasonsFor(item).forEach(reason => {
          totalsByReason[reason] = (totalsByReason[reason] || 0) + 1
          if (!byDay[day]) byDay[day] = {}
          byDay[day][reason] = (byDay[day][reason] || 0) + 1
        })
      })
      const days = Object.keys(byDay).sort()
      if (!days.length) return null
      // top 6 reasons by volume + "Other", so the chart doesn't get cluttered
      // with the long tail of carrier-specific sms responses
      const sortedReasons = Object.entries(totalsByReason).sort((a, b) => b[1] - a[1])
      const hasOther = sortedReasons.length > 6
      const reasonList = (hasOther ? sortedReasons.slice(0, 6) : sortedReasons).map(([reason]) => reason)
      const cols = hasOther ? [...reasonList, 'Other'] : reasonList
      return [
        ['Date', ...cols],
        ...days.map(day => {
          const dayData = byDay[day] || {}
          const row = [moment(day).format('MM/DD'), ...reasonList.map(reason => dayData[reason] || 0)]
          if (hasOther) {
            const otherCount = Object.entries(dayData)
              .filter(([reason]) => !reasonList.includes(reason))
              .reduce((sum, [, count]) => sum + count, 0)
            row.push(otherCount)
          }
          return row
        }),
      ]
    },
    alertsByTypeChart() {
      const counts = this.summary.type_unit_counts || {}
      let entries = Object.entries(counts).map(([type, v]) => [type, v.count || 0]).sort((a, b) => b[1] - a[1])
      if (!entries.length) return null
      if (entries.length > 8) {
        const top = entries.slice(0, 7)
        const otherTotal = entries.slice(7).reduce((sum, [, count]) => sum + count, 0)
        entries = [...top, ['Other', otherTotal]]
      }
      return this.barChartData('Type', 'Count', entries)
    },
    recentFailures() {
      return this.items_full
        .filter(item => this.isAlertFailed(item))
        .slice()
        .sort((a, b) => new Date(b.processed_time) - new Date(a.processed_time))
        .slice(0, 5)
        .map(item => ({
          key: item.movement_vehicle_id || `${item.alert_type}-${item.processed_time}`,
          timeLabel: moment(item.processed_time).format('MM/DD h:mma'),
          alert_type: item.alert_type,
          booking_id: item.booking_id || '',
          vehicle_id: item.vehicle_id || item.movement_vehicle_id || '',
          recipient: item.sms_name || '',
          contact: this.failureContact(item),
          reason: this.failureReason(item),
          preview: this.firstLine(item),
        }))
    },
    // sms_name holds the driver's name for driver-facing alerts like LateAlert
    topLateAlertsByDriver() {
      const counts = {}
      this.items_full
        .filter(item => item.alert_type === 'LateAlert')
        .forEach(item => {
          const name = (item.sms_name || '').trim()
          if (!name) return
          counts[name] = (counts[name] || 0) + 1
        })
      return Object.entries(counts)
        .map(([driver, count]) => ({ driver, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    },
    lateAlertsByDriverChart() {
      if (!this.topLateAlertsByDriver.length) return null
      return this.barChartData('Driver', 'Late Alerts', this.topLateAlertsByDriver.map(r => [r.driver, r.count]))
    },
    // MissingDVIR-PRE / MissingDVIR-POST cover pre-trip and post-trip inspection alerts
    topDvirAlertsByDriver() {
      const counts = {}
      this.items_full
        .filter(item => (item.alert_type || '').startsWith('MissingDVIR'))
        .forEach(item => {
          const name = (item.sms_name || '').trim()
          if (!name) return
          counts[name] = (counts[name] || 0) + 1
        })
      return Object.entries(counts)
        .map(([driver, count]) => ({ driver, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    },
    dvirAlertsByDriverChart() {
      if (!this.topDvirAlertsByDriver.length) return null
      return this.barChartData('Driver', 'DVIR Alerts', this.topDvirAlertsByDriver.map(r => [r.driver, r.count]))
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      },
    },
  },
}
</script>

<style scoped>
.error { background-color: rgba(255,0,0,0.25) }
.success { background-color: hsla(120,100%,75%,0.3) }

.white-link {
  color: black;
  font-size: 28px;
  text-decoration: none;
}

.ad-header-row {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 25px;
  padding: 16px 20px;
  position: relative;
  background: linear-gradient(180deg, #f4f8fc 0%, #e8f0f8 100%);
  border: 1px solid #d3e2f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.ad-header-left {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
}

.ad-header-right {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

/* below this width the absolutely-positioned title/buttons would overlap the
   centered date-nav content as it wraps, so stack everything into its own
   full-width line instead */
@media (max-width: 768px) {
  .ad-header-left,
  .ad-header-right {
    position: static;
    transform: none;
    width: 100%;
    justify-content: center;
    margin: 6px 0;
  }
}

.ad-chart-type-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ad-top-charts-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 20px;
  margin-top: 25px;
}

/* grid's default align-items:stretch gives both children the same height;
   the --fill children then use flex to fill that height exactly, instead of
   two independently-guessed pixel heights that never quite matched */
.ad-top-charts-row--fill {
  align-items: stretch;
}

.ad-chart-card--fill {
  display: flex;
  flex-direction: column;
  padding-top: 10px;
}

.ad-chart-fill-area {
  flex: 1;
  min-height: 300px;
  display: flex;
}

.ad-fill-gchart {
  width: 100%;
  height: 100%;
}

.ad-chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 20px;
  margin-top: 25px;
}

.ad-chart-card {
  border: solid 1px black;
  border-radius: 25px;
  padding: 15px;
}

.ad-chart-card--wide {
  grid-column: span 2;
}

.ad-chart-title {
  text-align: center;
  font-weight: bold;
  font-size: 22px;
  color: #545454;
  margin-bottom: 10px;
}

.ad-chart-empty {
  text-align: center;
  color: #888;
  padding: 60px 0;
}

.ad-mini-table {
  width: 100%;
  margin-top: 10px;
  border-collapse: collapse;
}

.ad-mini-table th {
  text-align: center;
  padding: 4px 8px;
  border-bottom: 2px solid #ccc;
  color: #545454;
  font-size: 13px;
}

.ad-mini-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #eee;
}

.ad-failure-time {
  white-space: nowrap;
  color: #666;
  font-size: 13px;
}

.ad-failure-reason {
  color: darkred;
  font-size: 13px;
}

.ad-failure-preview {
  color: #555;
  font-style: italic;
  font-size: 13px;
}
</style>
